import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { useCommon } from 'src/domains/common/Common';
import { EnvironmentState } from 'src/domains/layouts/state/environmentState/EnvironmentState';
import { WorldpayCheckoutInstance, WorldpayInitOptions } from './WorldpayFrameOptions';
import { WorldpayTabState, WorldpayPaymentMethodType } from './WorldpayTab.state';
import { loadScript } from './loadScript';
import {
    WorldpayForm,
    WorldpayFieldGroup,
    WorldpayFieldWrapper,
    WorldpayFieldLabel,
    WorldpayField,
    WorldpayTextInput,
    WorldpaySubmitButton,
} from './WorldpayTab.style';

const WORLDPAY_SDK_URL_TEST = 'https://try.access.worldpay.com/access-checkout/v2/checkout.js';
const WORLDPAY_SDK_URL_LIVE = 'https://access.worldpay.com/access-checkout/v2/checkout.js';

const FORM_ID = 'worldpay-card-form';
const PAN_ID = 'worldpay-card-pan';
const EXPIRY_ID = 'worldpay-card-expiry';
const CVV_ID = 'worldpay-card-cvv';

interface WorldpayNewCardFramePropsType {
    state: WorldpayTabState;
    hidden: boolean;
    paymentMethod: WorldpayPaymentMethodType;
}

export const WorldpayNewCardFrame = observer(
    'WorldpayNewCardFrame',
    ({ state, hidden, paymentMethod }: WorldpayNewCardFramePropsType): JSX.Element => {
        const common = useCommon();
        const { worldpayCheckout } = EnvironmentState.get(common).emotionTheme.star;
        const checkoutRef = React.useRef<WorldpayCheckoutInstance | null>(null);

        const styles: Record<string, Record<string, string>> = {};
        if (worldpayCheckout.input.color !== '') {
            styles['input'] = {
                ...(worldpayCheckout.input.color ? { color: worldpayCheckout.input.color } : {}),
                ...(worldpayCheckout.input.fontSize ? { 'font-size': worldpayCheckout.input.fontSize } : {}),
                ...(worldpayCheckout.input.fontWeight ? { 'font-weight': worldpayCheckout.input.fontWeight } : {}),
            };
        }
        if (worldpayCheckout.inputValid.color !== '') {
            styles['input.is-valid'] = { color: worldpayCheckout.inputValid.color };
        }
        if (worldpayCheckout.inputInvalid.color !== '') {
            styles['input.is-invalid'] = { color: worldpayCheckout.inputInvalid.color };
        }
        if (worldpayCheckout.inputFocus.color !== '') {
            styles['input.is-onfocus'] = { color: worldpayCheckout.inputFocus.color };
        }

        React.useEffect(() => {
            if (paymentMethod !== 'debit-card') {
                return;
            }

            const initSdk = async (): Promise<void> => {
                if (state.worldpayCardConfig === null) {
                    return;
                }
                const src =
                    state.worldpayCardConfig.environment === 'TEST' ? WORLDPAY_SDK_URL_TEST : WORLDPAY_SDK_URL_LIVE;

                await loadScript(src);

                if (window.Worldpay === undefined) {
                    throw new Error('Worldpay SDK not available after script load');
                }

                const init = window.Worldpay.checkout.init as unknown as (
                    options: WorldpayInitOptions,
                    callback: (error: unknown, checkout: WorldpayCheckoutInstance) => void
                ) => void;

                init(
                    {
                        id: state.worldpayCardConfig.checkoutId,
                        form: `#${FORM_ID}`,
                        fields: {
                            pan: { selector: `#${PAN_ID}`, placeholder: '4444 3333 2222 1111' },
                            expiry: { selector: `#${EXPIRY_ID}`, placeholder: 'MM/YY' },
                            cvv: { selector: `#${CVV_ID}`, placeholder: '123' },
                        },
                        enablePanFormatting: true,
                        acceptedCardBrands: ['maestro', 'mastercard', 'visa'],
                        ...(Object.keys(styles).length > 0 ? { styles } : {}),
                    },
                    (error, checkout) => {
                        if (error !== null && error !== undefined) {
                            console.error('worldpay-sdk-init-error', error);
                            state.handleSdkError();
                            return;
                        }
                        checkoutRef.current = checkout;
                        state.handleSdkReady();
                    }
                );
            };

            initSdk().catch((err) => {
                console.error('worldpay-frame-init', err);
                state.handleSdkError();
            });

            return (): void => {
                checkoutRef.current?.remove();
                checkoutRef.current = null;
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        const handleSubmit = (e: React.FormEvent): void => {
            e.preventDefault();
            const checkout = checkoutRef.current;
            if (checkout === null) {
                return;
            }
            state.handleSubmitting();
            checkout.generateSessions((error, sessions) => {
                if (error !== null && error !== undefined) {
                    console.error('worldpay-generate-session-error', error);
                    state.handleCardSessionError(error);
                    return;
                }
                void state.handleSessionState(sessions);
            });
        };

        if (paymentMethod !== 'debit-card') {
            return <></>;
        }

        return (
            <WorldpayForm
                id={FORM_ID}
                onSubmit={handleSubmit}
                style={hidden ? { display: 'none' } : undefined}
            >
                <WorldpayFieldWrapper>
                    <WorldpayFieldLabel htmlFor='worldpay-card-holder-name'>Cardholder name</WorldpayFieldLabel>
                    <WorldpayTextInput
                        id='worldpay-card-holder-name'
                        type='text'
                        autoComplete='cc-name'
                        value={state.cardHolderName}
                        onChange={(e): void => state.handleCardHolderNameChange(e.target.value)}
                        placeholder='Name on card'
                    />
                </WorldpayFieldWrapper>

                <WorldpayFieldWrapper>
                    <WorldpayFieldLabel htmlFor={PAN_ID}>Card number</WorldpayFieldLabel>
                    <WorldpayField id={PAN_ID} />
                </WorldpayFieldWrapper>

                <WorldpayFieldGroup>
                    <WorldpayFieldWrapper>
                        <WorldpayFieldLabel htmlFor={EXPIRY_ID}>Expiry date</WorldpayFieldLabel>
                        <WorldpayField id={EXPIRY_ID} />
                    </WorldpayFieldWrapper>
                    <WorldpayFieldWrapper>
                        <WorldpayFieldLabel htmlFor={CVV_ID}>CVV</WorldpayFieldLabel>
                        <WorldpayField id={CVV_ID} />
                    </WorldpayFieldWrapper>
                </WorldpayFieldGroup>

                <WorldpaySubmitButton
                    type='submit'
                    disabled={state.isSubmitting}
                >
                    {state.isSubmitting ? 'Processing...' : 'Pay Now'}
                </WorldpaySubmitButton>
            </WorldpayForm>
        );
    }
);
